// Sales, Kontakty (mockup v8, obrazovka 10): přehled jako v Notionu a Bývalé klientky.
import { confirmDialog } from "../../components/confirmHost.jsx";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Search, PhoneCall, ArrowUpRight, Users, List, LayoutGrid, RotateCcw } from "lucide-react";
import { DlazdiceFiltr, RozbalovaciVyber, EmptyState, Segment } from "../../components/ui.jsx";
import NastenkaKontaktu from "../../components/sales/NastenkaKontaktu.jsx";
import { toast } from "../../components/Toast.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { salesGet, salesPost, kdyText, casBezNuly, dm, dmr, telefonHezky, denKratce } from "../../components/sales/salesApi.js";
import { Pil, RadekKontaktu, KartaObal, Oznameni, StavPilulka, SpolupracePil } from "../../components/sales/salesUi.jsx";
// 1. 11. 2026 (mockup 8A a 8B): O klientce u bývalých klientek s klient_id.
import { OKlientceTlacitko, OKlientceOkno, smiOKlientce } from "../../components/sales/OKlientce.jsx";

const POHLEDY = [
  { key: "rozpracovane", label: "Rozpracované" }, { key: "dnes", label: "Volat dnes" }, { key: "odlozene", label: "Odložené" },
  { key: "zkusebni", label: "Zkušební" }, { key: "uzavrene", label: "Uzavřené" }, { key: "byvale", label: "Bývalé klientky" },
];
const KONTAKTOVANA = [{ key: "vse", label: "Všechny" }, { key: "0", label: "Zatím nikdy" }, { key: "1", label: "1x" }, { key: "2", label: "2x a víc" }];

// Seznam nebo Nástěnka (1. 10. 2026). Volba se pamatuje v prohlížeči, jen pro pohodlí.
const KLIC_REZIMU = "fw40_sales_kontakty_rezim";
function nactiRezim() { try { return localStorage.getItem(KLIC_REZIMU) === "nastenka" ? "nastenka" : "seznam"; } catch { return "seznam"; } }
const REZIMY = [
  { key: "seznam", label: <span className="inline-flex items-center gap-1.5"><List className="h-4 w-4" />Seznam</span> },
  { key: "nastenka", label: <span className="inline-flex items-center gap-1.5"><LayoutGrid className="h-4 w-4" />Nástěnka</span> },
];

function dalsiText(k) {
  if (k.stav === "zkusebni" && k.zkusebni) return `zkušební ${denKratce(k.zkusebni.datum)}`;
  if (["nezajem", "klientka", "neuzavreno"].includes(k.stav)) return "";
  if (!k.dalsi) return "";
  return `${kdyText(k.dalsi)}${k.dalsi_cas ? ` ${casBezNuly(k.dalsi_cas)}` : ""}`;
}

export default function SalesKontakty() {
  const uzka = useUzkaObrazovka();
  const navigate = useNavigate();
  const { nactiDnes } = useOutletContext();
  const [pohled, setPohled] = useState("rozpracovane");
  const [rezim, setRezimStav] = useState(nactiRezim);
  const nastenka = rezim === "nastenka";
  const setRezim = (r) => { setRezimStav(r); try { localStorage.setItem(KLIC_REZIMU, r); } catch { /* jen pohodlí */ } };
  // ?q= ze zvonečku (Otevřít kontakt u nerozhodnuté), 25. 10. 2026.
  const [parametry] = useSearchParams();
  const [q, setQ] = useState(() => parametry.get("q") || "");
  const [hledat, setHledat] = useState(() => parametry.get("q") || "");
  useEffect(() => { const v = parametry.get("q"); if (v) { setQ(v); setHledat(v); } }, [parametry]);
  const [kontaktovana, setKontaktovana] = useState("vse");
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [busyKlic, setBusyKlic] = useState("");
  const [oKlientce, setOKlientce] = useState(null);
  const ukazOKlientce = smiOKlientce();

  useEffect(() => { const t = setTimeout(() => setHledat(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  // 2. 10. 2026 (Láďa): Seznam i Nástěnka mají stejné dlaždice a filtr Zobrazit, mění se jen
  // obsah pod nimi. Při přepnutí zůstanou poslední počty, ať dlaždice neblikají třemi tečkami.
  const [pocty, setPocty] = useState(null);
  const nacti = useCallback(async () => {
    setChyba("");
    try {
      if (pohled === "byvale") setData({ byvale: await salesGet("byvale", { kontaktovana, q: hledat }) });
      else {
        const k = await salesGet("kontakty", { pohled, q: hledat });
        setData({ kontakty: k }); if (k?.pocty) setPocty(k.pocty);
      }
    } catch (e) { setChyba(e.message); }
  }, [pohled, hledat, kontaktovana]);
  useEffect(() => { setData(null); nacti(); }, [nacti]);
  useEffect(() => { const h = () => nacti(); window.addEventListener("fw40-sales-obnovit", h); return () => window.removeEventListener("fw40-sales-obnovit", h); }, [nacti]);

  const otevri = (id) => navigate(`/sales/kontakt/${id}`);
  const pridat = async (x) => {
    // 6. 10. 2026 (Láďa): před vrácením k volání potvrzení, ať se nic nestane omylem.
    const zkDatum = x.zkusebni?.datum ? denKratce(x.zkusebni.datum) : (x.datum_zk ? dm(x.datum_zk) : "");
    // 17. 10. 2026 (Láďa): bývalá klientka může mít hovor naplánovaný u jiného kontaktu, pak otázka na přepsání.
    let hovor = null;
    if (x.klient_id) { try { hovor = (await salesGet("hovor_klientky", { klient_id: x.klient_id }))?.hovor || null; } catch { hovor = null; } }
    const ok = hovor
      ? await confirmDialog({ title: "Přepsat naplánovaný hovor?", description: `${String(x.jmeno || "Klientka").split(" ")[0]} už má hovor naplánovaný na ${dmr(hovor.datum)}. Přesune se na dnes, druhý nevznikne.`, confirmLabel: "Přepsat na dnes", variant: "warning" })
      : await confirmDialog({ title: `Vrátit ${String(x.jmeno || "kontakt").split(" ")[0]} k volání?`, description: zkDatum ? `Má zkušební trénink ${zkDatum}. Trénink se neruší, kontakt jen přijde do dnešního plánu hovorů.` : "Kontakt přijde do dnešního plánu hovorů.", confirmLabel: "Ano, vrátit k volání", variant: "primary" });
    if (!ok) return null;
    setBusyKlic(x.klic);
    try {
      const r = x.klient_id ? await salesPost("klientka_k_volani", { klient_id: x.klient_id }) : await salesPost("k_volani", { id: x.lead_id });
      toast(`${x.jmeno} je v dnešním volání.`, "success");
      nactiDnes?.(); nacti();
      return r;
    } catch (e) { toast(e.message, "error"); return null; }
    finally { setBusyKlic(""); }
  };

  const dlazdice = (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
      <DlazdiceFiltr popisek="Rozpracované" hodnota={pocty?.rozpracovane ?? "…"} ton="znacka" zapnuto={pohled === "rozpracovane"} onClick={() => setPohled("rozpracovane")} />
      <DlazdiceFiltr popisek="Volat dnes" hodnota={pocty?.dnes ?? "…"} ton="pozor" zapnuto={pohled === "dnes"} onClick={() => setPohled("dnes")} />
      <DlazdiceFiltr popisek="Odložené" hodnota={pocty?.odlozene ?? "…"} ton="info" zapnuto={pohled === "odlozene"} onClick={() => setPohled("odlozene")} />
      <DlazdiceFiltr popisek="Zkušební" hodnota={pocty?.zkusebni ?? "…"} ton="ok" zapnuto={pohled === "zkusebni"} onClick={() => setPohled("zkusebni")} />
    </div>
  );
  const filtry = (
    <div className={`grid gap-3 ${pohled === "byvale" ? "lg:grid-cols-4" : "lg:grid-cols-[minmax(0,1fr)_minmax(0,260px)_minmax(0,340px)]"}`}>
      <div className="relative min-w-0">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="h-4 w-4" /></span>
        <input className="field !min-h-[56px] !rounded-2xl !pl-9" placeholder="Hledat jméno nebo telefon" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Hledat kontakt" />
      </div>
      <Segment items={REZIMY} value={rezim} onChange={setRezim} className="self-stretch" aria-label="Seznam nebo nástěnka" />
      <RozbalovaciVyber stitek="Zobrazit" items={POHLEDY} value={pohled} onChange={setPohled} aria-label="Zobrazit" />
      {pohled === "byvale" && <RozbalovaciVyber stitek="Kontaktována" items={KONTAKTOVANA} value={kontaktovana} onChange={setKontaktovana} aria-label="Kontaktována" />}
    </div>
  );

  const nacitani = <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-brand-light" />)}</div>;

  if (nastenka && pohled !== "byvale") {
    return (
      <div className="space-y-4 sm:space-y-6">
        {dlazdice}
        {filtry}
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        {!data ? nacitani : hledat && !(data.kontakty?.seznam || []).length
          ? <KartaObal><EmptyState icon={Users} title="Nic nenalezeno" hint="Zkus jen část jména nebo posledních pár číslic telefonu." /></KartaObal>
          : <NastenkaKontaktu key={hledat} seznam={data.kontakty?.seznam || []} uzka={uzka} onOtevrit={otevri} />}
      </div>
    );
  }

  // Bývalé klientky
  if (pohled === "byvale") {
    const seznam = data?.byvale?.seznam || [];
    return (
      <div className="space-y-4 sm:space-y-6">
        {filtry}
        <Oznameni ton="info">Sem se klientka dostane sama, když <b>skončila nebo přerušila spolupráci</b> nebo <b>se po zkušebním tréninku neuzavřela</b>. Owner, manažer a CSM ji sem můžou poslat i ručně z karty klientky, třeba s datem. Cílem hovoru je domluvit návrat, se zkušebním tréninkem nebo bez něj. Blacklist se tu nikdy neukazuje.</Oznameni>
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        {!data ? nacitani : !seznam.length ? <KartaObal><EmptyState icon={Users} title="Nikdo tu není" hint="Zkus jiný filtr Kontaktována." /></KartaObal> : uzka ? (
          <KartaObal>
            {seznam.map((x, i) => (
              <div key={x.klic} className={`space-y-2 px-4 py-3 ${i ? "border-t border-slate-100" : ""}`}>
                <div className="text-base font-bold text-slate-900">{x.jmeno}</div>
                <div className="flex flex-wrap gap-1.5">
                  <Pil ton={x.duvod.ton} casti={x.duvod.text} />
                  {x.detail && <Pil ton="neutralni" casti={x.detail} />}
                  <Pil ton="neutralni" casti={["kontaktována", `${x.kontaktovana}x`]} />
                </div>
                <div className="flex gap-2">
                  {ukazOKlientce && x.klient_id && <OKlientceTlacitko onClick={() => setOKlientce(x)} className="flex-[2]" />}
                  {x.naplanovano
                    ? (x.lead_id && <button type="button" className="btn-secondary min-w-0 flex-[3]" onClick={() => otevri(x.lead_id)}><ArrowUpRight className="h-4 w-4" />Otevřít kartu</button>)
                    : <button type="button" className="btn-secondary min-w-0 flex-[3]" onClick={() => pridat(x)} disabled={busyKlic === x.klic} aria-busy={busyKlic === x.klic}><PhoneCall className="h-4 w-4" />Přidat k volání</button>}
                </div>
              </div>
            ))}
          </KartaObal>
        ) : (
          <KartaObal>
            <div className="p-3 sm:p-4"><div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-3 py-2">Jméno</th><th className="px-3 py-2">Proč tu je</th><th className="px-3 py-2">Detail</th><th className="px-3 py-2">Trenér</th><th className="px-3 py-2">Kontaktována</th><th className="px-3 py-2" /></tr></thead>
                <tbody>
                  {seznam.map((x) => (
                    <tr key={x.klic} className="border-t border-slate-100">
                      <td className="px-3 py-2.5 font-bold text-brand-deep">{x.jmeno}<div className="text-xs font-normal tabular-nums text-slate-500">{telefonHezky(x.telefon)}</div></td>
                      <td className="px-3 py-2.5"><Pil ton={x.duvod.ton} casti={x.duvod.text} mala /></td>
                      <td className="px-3 py-2.5 text-slate-600">{x.detail}</td>
                      <td className="px-3 py-2.5">{x.trener}</td>
                      <td className="px-3 py-2.5 tabular-nums">{x.kontaktovana}x</td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                        {ukazOKlientce && x.klient_id && <OKlientceTlacitko onClick={() => setOKlientce(x)} className="btn-sm !min-h-0" />}
                        {x.naplanovano
                          ? (x.lead_id && <button type="button" className="btn-secondary btn-sm" onClick={() => otevri(x.lead_id)}><ArrowUpRight className="h-4 w-4" />Otevřít kartu</button>)
                          : <button type="button" className="btn-secondary btn-sm whitespace-nowrap" onClick={() => pridat(x)} disabled={busyKlic === x.klic} aria-busy={busyKlic === x.klic}><PhoneCall className="h-4 w-4" />Přidat k volání</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></div>
          </KartaObal>
        )}
        {oKlientce && (
          <OKlientceOkno key={oKlientce.klic} klientId={oKlientce.klient_id} jmeno={oKlientce.jmeno} onClose={() => setOKlientce(null)} />
        )}
      </div>
    );
  }

  const seznam = data?.kontakty?.seznam || [];
  return (
    <div className="space-y-4 sm:space-y-6">
      {dlazdice}
      {filtry}
      {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
      {!data ? nacitani : !seznam.length ? <KartaObal><EmptyState icon={Users} title={hledat ? "Nic nenalezeno" : "Tady nikdo není"} hint={hledat ? "Zkus jen část jména nebo posledních pár číslic telefonu." : "Zkus jiný pohled v Zobrazit."} /></KartaObal> : uzka ? (
        <KartaObal>
          {seznam.map((k, i) => (
            <RadekKontaktu key={k.id} prvni={i === 0} jmeno={k.jmeno} onOtevrit={() => otevri(k.id)} poznamka={k.poznamka}
              pilulky={<><StavPilulka k={k} /><SpolupracePil k={k} />{k.znovu && <Pil ton="pozor" ikona={RotateCcw} casti={["znovu", dm(k.znovu.datum)]} />}{dalsiText(k) && <Pil ton="neutralni" casti={["další kontakt", dalsiText(k)]} />}</>} />
          ))}
        </KartaObal>
      ) : (
        <KartaObal>
          <div className="p-3 sm:p-4"><div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-3 py-2">Jméno</th><th className="px-3 py-2">Stav</th><th className="px-3 py-2">Další kontakt</th><th className="px-3 py-2">Zdroj</th><th className="px-3 py-2">Přišla</th><th className="px-3 py-2">Poslední poznámka</th><th className="px-3 py-2" /></tr></thead>
              <tbody>
                {seznam.map((k) => (
                  <tr key={k.id} className="border-t border-slate-100">
                    <td className="whitespace-nowrap px-3 py-2.5 font-bold text-brand-deep">{k.jmeno}</td>
                    <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1"><StavPilulka k={k} mala /><SpolupracePil k={k} mala /></div></td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-slate-800">{dalsiText(k)}</td>
                    <td className="px-3 py-2.5 text-slate-600">{k.znovu ? <div className="flex flex-col"><span>{k.znovu.zdroj || k.zdroj}</span>{k.zdroj && k.zdroj !== k.znovu.zdroj && <span className="text-[11px] text-slate-400">dřív {k.zdroj}</span>}</div> : k.zdroj}</td>
                    <td className="px-3 py-2.5 tabular-nums text-slate-600">{k.znovu
                      ? <div className="flex flex-col gap-0.5"><Pil ton="pozor" mala ikona={RotateCcw} casti={`znovu ${dm(k.znovu.datum)}`} /><span className="whitespace-nowrap text-[11px] text-slate-400">poprvé {dmr(k.znovu.prvni)}</span></div>
                      : dm(k.vytvoreno)}</td>
                    <td className="px-3 py-2.5 text-slate-600">{k.poznamka}</td>
                    <td className="px-3 py-2.5 text-right"><button type="button" className="btn-ikona" onClick={() => otevri(k.id)} aria-label={`Otevřít kartu ${k.jmeno}`} title="Otevřít kartu"><ArrowUpRight className="h-5 w-5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></div>
        </KartaObal>
      )}
    </div>
  );
}
